"use client"

import React from 'react'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'
import { UITypography } from '../display/ui-typography'
import { UICollapsible, UICollapsibleContent, UICollapsibleTrigger } from '../layout/ui-collapsible'
import { FileText, Globe, Link, BookOpen, ExternalLink, ChevronDown } from 'lucide-react'

// ============================================================================
// Types & Interfaces
// ============================================================================

export type UISourceType = 'document' | 'webpage' | 'api' | 'file' | 'book'

export interface UISourceData {
  id: string
  title: string
  description?: string
  url?: string
  type: UISourceType
  metadata?: {
    author?: string
    date?: Date | string
    pageNumber?: number
    section?: string
  }
}

export interface UISourcesProps extends React.ComponentPropsWithoutRef<typeof UICollapsible> {
  children?: React.ReactNode
  locale?: 'ko' | 'en'
  className?: string
}

export interface UISourcesTriggerProps extends React.ComponentPropsWithoutRef<typeof UICollapsibleTrigger> {
  count?: number
  locale?: 'ko' | 'en'
  className?: string
}

export interface UISourcesContentProps extends React.ComponentPropsWithoutRef<typeof UICollapsibleContent> {
  children?: React.ReactNode
  className?: string
}

export interface UISourceProps extends React.ComponentPropsWithoutRef<'div'> {
  source: UISourceData
  locale?: 'ko' | 'en'
  showMetadata?: boolean
  showDescription?: boolean
  onSourceClick?: (source: UISourceData) => void
  className?: string
}

// ============================================================================
// Utility Functions
// ============================================================================

const getIconForType = (type: UISourceType) => {
  const iconMap = {
    document: FileText,
    webpage: Globe,
    api: Link,
    file: FileText,
    book: BookOpen
  }
  return iconMap[type]
}

// ============================================================================
// Main Components (Composition-Based)
// ============================================================================

/**
 * UISources - Root container for sources
 * Based on shadcn.io/ai Sources with minimal styling
 */
export const UISources = React.forwardRef<
  React.ElementRef<typeof UICollapsible>,
  UISourcesProps
>(({ children, locale = 'ko', className, defaultOpen = true, ...props }, ref) => {
  return (
    <UICollapsible
      ref={ref}
      defaultOpen={defaultOpen}
      className={cn("not-prose text-primary text-xs", className)}
      {...props}
    >
      {children}
    </UICollapsible>
  )
})

UISources.displayName = "UISources"

/**
 * UISourcesTrigger - Collapsible trigger with count badge
 * Simplified version following shadcn.io/ai pattern
 */
export const UISourcesTrigger = React.forwardRef<
  React.ElementRef<typeof UICollapsibleTrigger>,
  UISourcesTriggerProps
>(({ count, locale = 'ko', className, children, ...props }, ref) => {
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <UICollapsibleTrigger
      ref={ref}
      asChild
      {...props}
    >
      <button
        className={cn(
          "flex items-center gap-2 text-sm font-medium transition-all duration-200",
          "hover:opacity-80",
          className
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <FileText className="h-4 w-4" />
        <span>
          {children || (locale === 'ko' ? '참조 출처' : 'Sources')}
          {count !== undefined && ` (${count})`}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 transition-transform duration-200",
            isOpen && "rotate-180"
          )}
        />
      </button>
    </UICollapsibleTrigger>
  )
})

UISourcesTrigger.displayName = "UISourcesTrigger"

/**
 * UISourcesContent - Container for source items
 * Simplified with minimal styling
 */
export const UISourcesContent = React.forwardRef<
  React.ElementRef<typeof UICollapsibleContent>,
  UISourcesContentProps
>(({ children, className, ...props }, ref) => {
  return (
    <UICollapsibleContent
      ref={ref}
      className={cn(
        "mt-2 space-y-2.5 transition-all duration-200",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    >
      {children}
    </UICollapsibleContent>
  )
})

UISourcesContent.displayName = "UISourcesContent"

/**
 * UISource - Individual source item
 * Using simple div with manual glassmorphism instead of UICard to avoid hover issues
 */
export const UISource = React.forwardRef<
  HTMLDivElement,
  UISourceProps
>((
  {
    source,
    locale = 'ko',
    showMetadata = false,
    showDescription = false,
    onSourceClick,
    className,
    ...props
  },
  ref
) => {
  const Icon = getIconForType(source.type)

  return (
    <UIBox
      ref={ref}
      className={cn(
        // Manual glassmorphism matching UICard but without hover transforms
        "rounded-lg transition-all duration-200",
        "bg-background/80 dark:bg-gray-900/50",
        "border border-gray-200 dark:border-gray-800",
        "ring-1 ring-white/10 dark:ring-white/5",
        "shadow-sm shadow-gray-950/5 dark:shadow-black/20",
        "p-2.5",
        // Hover effects without transforms
        onSourceClick && [
          "cursor-pointer",
          "hover:bg-background/90 dark:hover:bg-background/60",
          "hover:shadow-md hover:shadow-gray-950/10 dark:hover:shadow-black/30",
          "hover:border-gray-300 dark:hover:border-gray-700",
          "hover:ring-2 hover:ring-white/20 dark:hover:ring-white/10"
        ],
        className
      )}
      onClick={() => onSourceClick?.(source)}
      {...props}
    >
        <UIBox className="flex items-center gap-2.5">
          {/* Icon */}
          <Icon className="h-4 w-4 flex-shrink-0 text-muted-foreground dark:text-gray-400" />

          {/* Content */}
          <UIBox className="flex-1 min-w-0">
            {/* Title */}
            <UITypography
              variant="body2"
              weight="medium"
              className="line-clamp-1 leading-tight"
            >
              {source.title}
            </UITypography>

            {/* Metadata - Simple single line */}
            {showMetadata && source.metadata?.author && (
              <UITypography
                variant="caption"
                className="text-muted-foreground dark:text-gray-400 leading-tight"
              >
                {source.metadata.author}
              </UITypography>
            )}

            {/* Description - Optional */}
            {showDescription && source.description && (
              <UITypography
                variant="caption"
                className="line-clamp-2 text-muted-foreground dark:text-gray-400 mt-1"
              >
                {source.description}
              </UITypography>
            )}
          </UIBox>

          {/* External Link Icon */}
          {source.url && (
            <ExternalLink className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground dark:text-gray-400" />
          )}
        </UIBox>
    </UIBox>
  )
})

UISource.displayName = "UISource"